/**
 * «Revisar el dibujo»: lee las entidades de la modelspace del documento que el visor YA tiene abierto (no se vuelve a
 * parsear el archivo) y las reduce a lo que necesita `diagnosticarDibujo` de shared: tipo, capa, caja, largo y puntos en
 * UNIDADES DEL DIBUJO (factor 1), más el bloque (nombre crudo, rotación, escala) de las inserciones.
 *
 * También saca la huella de la DEFINICIÓN de cada bloque con inserciones (para detectar el mismo dibujo con dos nombres).
 * Si la tabla de bloques no se puede recorrer, `huellas` queda null y ese chequeo simplemente no aparece.
 *
 * Cede el hilo cada 4000 elementos y se puede cancelar. NO tiñe nada ni cambia el documento (solo lee).
 */
import { huellaDeDefinicion, type EntidadDelDibujo, type HuellaDeBloque } from '@supernova/shared';

type Pt = [number, number];
type AnyEnt = Record<string, unknown> & {
  objectId?: string;
  layer?: string;
  geometricExtents?: { min: { x: number; y: number }; max: { x: number; y: number } };
};
type Modulo = Record<string, unknown>;
type Iterable_ = { newIterator(): Iterable<unknown> };
export interface BaseDelVisor {
  tables: { blockTable: Iterable_ & { modelSpace: Iterable_ } };
}

/** Polilíneas con más vértices que esto no entran al análisis de repetidos (cuestan mucha memoria y casi nunca se repiten). */
const MAX_VERTICES = 2000;
const TIPOS_CON_TRAZO = new Set(['LINE', 'LWPOLYLINE', 'POLYLINE', 'ARC', 'CIRCLE', 'ELLIPSE']);

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const pt = (p: unknown): Pt | null => {
  const o = p as { x?: unknown; y?: unknown } | null | undefined;
  const x = num(o?.x);
  const y = num(o?.y);
  return x == null || y == null ? null : [x, y];
};
const cmp = (a: Pt, b: Pt) => a[0] - b[0] || a[1] - b[1];
const alFinalDelHilo = () => new Promise<void>((ok) => setTimeout(ok, 0));

/** Misma normalización que `firmasDxf` (abierta: extremo menor primero; cerrada: vértice menor y sentido menor). */
function normalizaPolilinea(pts: Pt[], cerrada: boolean): Pt[] {
  if (pts.length < 2) return pts;
  if (!cerrada) return cmp(pts[0]!, pts[pts.length - 1]!) <= 0 ? pts : [...pts].reverse();
  let ini = 0;
  for (let i = 1; i < pts.length; i++) if (cmp(pts[i]!, pts[ini]!) < 0) ini = i;
  const adelante = pts.map((_, k) => pts[(ini + k) % pts.length]!);
  const atras = pts.map((_, k) => pts[(ini - k + pts.length) % pts.length]!);
  for (let k = 1; k < pts.length; k++) {
    const c = cmp(adelante[k]!, atras[k]!);
    if (c !== 0) return c < 0 ? adelante : atras;
  }
  return adelante;
}

const largoDe = (pts: Pt[], cerrada: boolean): number => {
  let s = 0;
  for (let i = 1; i < pts.length; i++) s += Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]);
  if (cerrada && pts.length > 2) s += Math.hypot(pts[0]![0] - pts[pts.length - 1]![0], pts[0]![1] - pts[pts.length - 1]![1]);
  return s;
};

function cajaDe(e: AnyEnt, pts: Pt[] | null): [number, number, number, number] | null {
  try {
    const mn = pt(e.geometricExtents?.min);
    const mx = pt(e.geometricExtents?.max);
    if (mn && mx) return [mn[0], mn[1], mx[0], mx[1]];
  } catch {
    /* sin extensión: se usa lo que haya */
  }
  if (!pts?.length) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const [x, y] of pts) {
    if (x < x0) x0 = x;
    if (y < y0) y0 = y;
    if (x > x1) x1 = x;
    if (y > y1) y1 = y;
  }
  return [x0, y0, x1, y1];
}

/** Reduce una entidad a lo que usa el diagnóstico. null si no hay forma de ubicarla. */
function leerEntidad(e: AnyEnt, es: (e: unknown, n: string) => boolean): EntidadDelDibujo | null {
  const handle = e.objectId;
  if (!handle) return null;
  const capa = String(e.layer ?? '0');
  let tipo = 'OTRO';
  let puntos: Pt[] | undefined;
  let largo = 0;
  let bloque: EntidadDelDibujo['bloque'];
  let ref: Pt[] | null = null;
  try {
    if (es(e, 'AcDbLine')) {
      const a = pt(e.startPoint);
      const b = pt(e.endPoint);
      if (a && b) {
        tipo = 'LINE';
        puntos = cmp(a, b) <= 0 ? [a, b] : [b, a];
        largo = Math.hypot(b[0] - a[0], b[1] - a[1]);
      }
    } else if (es(e, 'AcDbPolyline') || es(e, 'AcDb2dPolyline') || es(e, 'AcDb3dPolyline')) {
      const lw = es(e, 'AcDbPolyline');
      tipo = lw ? 'LWPOLYLINE' : 'POLYLINE';
      const k = num(e.numberOfVertices) ?? 0;
      if (k > 0 && k <= MAX_VERTICES) {
        const v: Pt[] = [];
        for (let i = 0; i < k; i++) {
          const p = pt(lw ? (e as { getPoint2dAt(i: number): unknown }).getPoint2dAt(i) : (e as { getPointAt(i: number): unknown }).getPointAt(i));
          if (p) v.push(p);
        }
        if (v.length === k) {
          const cerrada = e.closed === true;
          puntos = normalizaPolilinea(v, cerrada);
          largo = largoDe(v, cerrada); // sin bulges: se documenta, no se inventa
        }
      }
    } else if (es(e, 'AcDbArc')) {
      const c = pt(e.center);
      const a = pt(e.startPoint);
      const b = pt(e.endPoint);
      const r = num(e.radius);
      if (c && a && b) {
        tipo = 'ARC';
        puntos = [c, a, b];
        const ini = num(e.startAngle) ?? 0;
        const fin = num(e.endAngle) ?? 0;
        let d = fin - ini;
        while (d < 0) d += Math.PI * 2;
        largo = (r ?? 0) * d;
      }
    } else if (es(e, 'AcDbCircle')) {
      const c = pt(e.center);
      const r = num(e.radius);
      if (c && r != null) {
        tipo = 'CIRCLE';
        puntos = [c, [c[0] + r, c[1]]];
        largo = 2 * Math.PI * r;
      }
    } else if (es(e, 'AcDbEllipse')) {
      const c = pt(e.center);
      const a = num(e.majorAxisRadius);
      const b = num(e.minorAxisRadius);
      if (c && a != null && b != null) {
        tipo = 'ELLIPSE';
        puntos = [c, [c[0] + a, c[1] + b]];
        largo = Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b))); // Ramanujan
      }
    } else if (es(e, 'AcDbBlockReference')) {
      const p = pt(e.position);
      if (p) {
        tipo = 'INSERT';
        const s = e.scaleFactors as { x?: unknown; y?: unknown } | undefined;
        bloque = { crudo: String(e.blockName ?? ''), rot: num(e.rotation) ?? 0, sx: num(s?.x) ?? 1, sy: num(s?.y) ?? 1 };
        ref = [p];
      }
    } else if (es(e, 'AcDbMText')) tipo = 'MTEXT';
    else if (es(e, 'AcDbText')) tipo = 'TEXT';
    else if (es(e, 'AcDbDimension')) tipo = 'DIMENSION';
    else if (es(e, 'AcDbHatch')) tipo = 'HATCH';
    else if (es(e, 'AcDbPoint')) tipo = 'POINT';
  } catch {
    puntos = undefined;
    largo = 0;
  }
  const caja = cajaDe(e, puntos ?? ref);
  if (!caja) return null;
  if (!(largo > 0) || !TIPOS_CON_TRAZO.has(tipo)) {
    // Sin trazo medible, no hace falta guardar sus puntos.
    puntos = undefined;
    largo = TIPOS_CON_TRAZO.has(tipo) ? largo : 0;
  }
  const out: EntidadDelDibujo = { handle, tipo, capa, caja, largo };
  if (puntos) out.puntos = puntos;
  if (bloque) out.bloque = bloque;
  return out;
}

/** Huella de la definición de cada bloque que tiene inserciones en el dibujo. null si no se puede recorrer la tabla. */
async function huellasDeBloques(db: BaseDelVisor, es: (e: unknown, n: string) => boolean, usados: Set<string>, maxTrazos: number, o: { signal?: AbortSignal }): Promise<HuellaDeBloque[] | null> {
  const out: HuellaDeBloque[] = [];
  try {
    let n = 0;
    for (const rec of db.tables.blockTable.newIterator()) {
      if (o.signal?.aborted) throw new DOMException('cancelado', 'AbortError');
      if (++n % 50 === 0) await alFinalDelHilo();
      const r = rec as { name?: unknown } & Iterable_;
      const nombre = typeof r.name === 'string' ? r.name : '';
      if (!nombre || !usados.has(nombre) || typeof r.newIterator !== 'function') continue;
      const trazos: { tipo: string; puntos: Pt[] }[] = [];
      let demasiados = false;
      for (const ent of r.newIterator()) {
        if (trazos.length >= maxTrazos) {
          demasiados = true;
          break;
        }
        const x = leerEntidad(ent as AnyEnt, es);
        if (x?.puntos && x.largo > 0) trazos.push({ tipo: x.tipo, puntos: x.puntos as unknown as Pt[] });
      }
      out.push({ crudo: nombre, limpio: nombre, huella: demasiados ? null : huellaDeDefinicion(trazos, 0.01), trazos: trazos.length });
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    return null;
  }
  return out;
}

export interface EntidadesLeidas {
  entidades: EntidadDelDibujo[];
  /** null = no se pudo recorrer la tabla de bloques. `limpio` = crudo: el llamador lo cambia por el del inventario. */
  huellas: HuellaDeBloque[] | null;
}

/**
 * Lee el documento abierto. `dm` = el módulo `@mlightcad/data-model` ya cargado por el visor (para reconocer los tipos).
 * `maxTrazosPorBloque` = `diag_bloque_huella_max_trazos` de Calibración.
 */
export async function entidadesDelDibujo(
  db: BaseDelVisor,
  dm: Modulo,
  o: { signal?: AbortSignal; maxTrazosPorBloque?: number } = {},
): Promise<EntidadesLeidas> {
  const es = (e: unknown, nombre: string) => {
    const C = dm[nombre] as (abstract new (...a: never[]) => unknown) | undefined;
    return !!C && e instanceof C;
  };
  const entidades: EntidadDelDibujo[] = [];
  const usados = new Set<string>();
  let n = 0;
  for (const ent of db.tables.blockTable.modelSpace.newIterator()) {
    if (o.signal?.aborted) throw new DOMException('cancelado', 'AbortError');
    if (++n % 4000 === 0) await alFinalDelHilo();
    const x = leerEntidad(ent as AnyEnt, es);
    if (!x) continue;
    if (x.bloque) usados.add(x.bloque.crudo);
    entidades.push(x);
  }
  const huellas = await huellasDeBloques(db, es, usados, o.maxTrazosPorBloque ?? 2000, o);
  return { entidades, huellas };
}
